import type { Audit } from "@/types";
import { maxStepError, viewportLabel } from "./replay/helpers";

export default function FailedInteractionMessage({ audit }: { audit: Audit }) {
  if (audit.status !== "failed") {
    return null;
  }

  const failedInteraction = audit.interactions.find((interaction) => interaction.status === "failed");

  if (!failedInteraction) {
    return null;
  }

  if (maxStepError(failedInteraction)) {
    return (
      <section className="card mb-6">
        <div className="card-body">
          <div className="flex items-start gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary">
              i
            </div>
            <div className="flex-1">
              <h2 className="mb-2 text-lg font-semibold text-gray-800">
                Step Limit Reached ({viewportLabel(failedInteraction.viewport)})
              </h2>
              <p className="text-gray-600">
                We captured what we could within the interaction steps limit and will analyze the UX from that data.
              </p>

              <FailureDetail title="What happened" tone="info">
                {failedInteraction.finish_reason || audit.failure_summary || "The interaction reached the step limit."}
              </FailureDetail>

              <RecommendationList
                title="How to get better results"
                items={[
                  "Try starting from a more specific page, like a login page if authentication is needed.",
                  "Break down complex tasks into smaller, more focused objectives.",
                  "Make your task description more specific and actionable.",
                ]}
              />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="card mb-6">
      <div className="card-body">
        <div className="flex items-start gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-error-light text-error">
            !
          </div>
          <div className="flex-1">
            <h2 className="mb-2 text-lg font-semibold text-gray-800">
              Task Could Not Be Completed ({viewportLabel(failedInteraction.viewport)})
            </h2>

            <FailureDetail title="Error Details" tone="error">
              {failedInteraction.finish_reason || audit.failure_summary || "Unknown error"}
            </FailureDetail>

            <RecommendationList title="Recommendations" items={recommendationsFor(failedInteraction.finish_reason)} />
          </div>
        </div>
      </div>
    </section>
  );
}

function FailureDetail({ children, title, tone }: { children: string; title: string; tone: "error" | "info" }) {
  const classes =
    tone === "error" ? "border-red-100 bg-red-50 text-red-800" : "border-primary-100 bg-primary-50 text-primary-800";

  return (
    <div className={`mt-4 rounded-md border p-4 ${classes}`}>
      <h3 className="mb-2 font-medium">{title}</h3>
      <p className="text-gray-700">{children}</p>
    </div>
  );
}

function RecommendationList({ items, title }: { items: string[]; title: string }) {
  return (
    <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-4">
      <h3 className="mb-2 font-medium text-gray-800">{title}</h3>
      <ul className="list-disc space-y-2 pl-5 text-gray-700">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function recommendationsFor(finishReason: string | null) {
  if (finishReason === "Our model is overloaded, please try again later.") {
    return [
      "Wait a few minutes and try running the interaction again.",
      "Check the system status for any ongoing issues.",
      "Consider running the interaction during off-peak hours.",
    ];
  }

  if (finishReason === "An unexpected error happened, please try again.") {
    return [
      "We have been notified and will investigate the issue. Please try again later.",
      "Contact support at support@scoutux.io if the problem persists.",
    ];
  }

  if (finishReason === "The URL is invalid or the website is not available.") {
    return [
      "Please check if the URL is correct and the website is up.",
      "Contact support at support@scoutux.io if the problem persists.",
    ];
  }

  return [
    "Verify that the task instructions are clear and achievable.",
    "Contact support at support@scoutux.io if you need assistance.",
  ];
}
